// Library Methods
import { createContext, useState, useContext, useEffect, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";

// Hooks
import { useClinicDevices } from "src/services/devices/hooks/useClinicDevices";

// Types
import { DeviceType } from "src/services/devices/types";

// Contexts
import { useClinicContext } from "../clinic/ClinicProvider";

const DevicesContext = createContext({
  devices: [] as Array<DeviceType> | undefined,
  unlinkedDevices: [] as Array<DeviceType>,
  unlinkedDeviceOptions: [] as Array<{ displayName: string; value: string }>,
  isLoadingDevices: false,
  isFetchingDevices: false,
  isRefetchingDevices: false,
  isErrorDevices: false,
  setDevices: (_: Array<DeviceType>) => {},
  refetchDevices: () => {},
});

export const useDevicesContext = () => {
  const context = useContext(DevicesContext);
  if (!context) {
    throw new Error(
      "useDevicesContext must be used within DevicesContextProvider"
    );
  }

  return context;
};

type DevicesProviderType = {
  children: React.ReactNode;
};

// A context that contains the most up-to-date device information based on the signalR connection
const DevicesProvider = ({ children }: DevicesProviderType) => {
  const queryClient = useQueryClient();
  const { clinicInfo } = useClinicContext();
  const { pathname } = useLocation();
  const {
    data: initialDevices,
    isLoading: isLoadingDevices,
    isFetching: isFetchingDevices,
    isRefetching: isRefetchingDevices,
    isError: isErrorDevices,
    refetch: refetchDevices,
  } = useClinicDevices(clinicInfo?.id, !pathname.includes("pdf"));

  const [devices, setDevices] = useState<Array<DeviceType>>();

  // Sync the rendered device list whenever fresh query data comes in — both on
  // initial load and on refetches (e.g. the invalidation that follows a
  // link/unlink mutation) — not just the first time.
  useEffect(() => {
    if (initialDevices && !isLoadingDevices) {
      setDevices(initialDevices);
    }
  }, [initialDevices, isLoadingDevices]);

  // Unlinked devices in original type
  const unlinkedDevices: Array<DeviceType> = useMemo(
    () =>
      (devices ?? [])?.filter(
        (device) => !device.associatedExam && device.webserviceCompatible
      ),
    [devices]
  );

  // Formatted devices for dropdown display
  const unlinkedDeviceOptions: Array<{ displayName: string; value: string }> =
    useMemo(
      () =>
        devices
          ?.filter(
            (device) => !device.associatedExam && device.webserviceCompatible
          )
          ?.sort((a, b) => a?.alias?.localeCompare(b?.alias))
          ?.map((device) => ({
            displayName: device.alias,
            value: device.id,
          })) ?? [],
      [devices]
    );

  // Reset the devices when the clinic is changed
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["clinic-devices"] });
    setDevices(initialDevices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicInfo?.id]);

  return (
    <DevicesContext.Provider
      value={{
        devices,
        unlinkedDevices,
        unlinkedDeviceOptions,
        isLoadingDevices,
        isFetchingDevices,
        isRefetchingDevices,
        isErrorDevices,
        setDevices,
        refetchDevices,
      }}
    >
      {children}
    </DevicesContext.Provider>
  );
};

export default DevicesProvider;
